import { Tooltip, Tag, Empty, Spin } from "antd";
import { AppstoreOutlined, DownOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import { readable } from "./workspaceUtils";

const accountLabel = (account) => account?.name || "Workspace";

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const WorkspacePicker = () => {
  const navigate = useNavigate();
  const {
    workspaces,
    selectedWorkspaceId,
    selectedWorkspace,
    selectWorkspace,
    loading,
  } = useWorkspace();
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);

  const workspaceOptions = workspaces.filter((account) => account?.id);

  const handleWorkspaceSelect = (workspaceId) => {
    if (!workspaceId || workspaceId === selectedWorkspaceId) {
      setOpen(false);
      return;
    }

    selectWorkspace(workspaceId);
    setOpen(false);
    navigate({
      pathname: paths.pmDashboard,
      search: `?wid=${encodeURIComponent(workspaceId)}`,
    });
  };

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!pickerRef.current) return;
      if (!pickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  return (
    <div
      ref={pickerRef}
      className="pm-pa-menu"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Tooltip title="Workspace">
        <button
          type="button"
          className={`pm-pa-trigger${open ? " open" : ""}`}
          onClick={() => setOpen((value) => !value)}
          aria-label="Select workspace"
          aria-expanded={open}
        >
          <AppstoreOutlined />
          <span className="pm-pa-trigger-copy">
            <span className="pm-pa-trigger-kicker">Workspace</span>
            <span className="pm-pa-trigger-name">
              {selectedWorkspace ? accountLabel(selectedWorkspace) : "Select workspace"}
            </span>
          </span>
          <DownOutlined className="pm-pa-trigger-caret" />
        </button>
      </Tooltip>

      {open ? (
        <div className="pm-pa-popdown">
          <div className="pm-pa-popdown-head">
            <div className="pm-pa-popdown-orb">
              <AppstoreOutlined />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="pm-pa-popdown-kicker">Active Workspace</div>
              <div className="pm-pa-popdown-title">
                {selectedWorkspace
                  ? accountLabel(selectedWorkspace)
                  : "Workspace"}
              </div>
              <div className="pm-pa-popdown-subtitle">
                {selectedWorkspace ? (
                  <>
                    {readable(selectedWorkspace.product, "Product")} •{" "}
                    {readable(selectedWorkspace.accountType, "Workspace type")}
                  </>
                ) : (
                  "Choose the workspace this session should use"
                )}
              </div>
            </div>
          </div>
          <div className="pm-pa-popdown-strip">
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              {workspaces.length} available
            </Tag>
            {!isPersonalAccount(selectedWorkspace) && selectedWorkspace?.organizationId ? (
              <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                Business
              </Tag>
            ) : null}
          </div>
          <div className="pm-pa-list" aria-label="Available workspaces">
            {loading ? (
              <div className="pm-pa-state">
                <Spin size="small" />
                <span>Loading workspaces</span>
              </div>
            ) : workspaceOptions.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No workspaces"
                style={{ margin: "8px 0" }}
              />
            ) : (
              workspaceOptions.map((account) => {
                const selected = account.id === selectedWorkspaceId;

                return (
                  <button
                    type="button"
                    key={account.id}
                    className={`pm-pa-option${selected ? " is-selected" : ""}`}
                    onClick={() => handleWorkspaceSelect(account.id)}
                    aria-current={selected ? "true" : undefined}
                  >
                    <span className="pm-pa-option-copy">
                      <span className="pm-pa-option-name">{accountLabel(account)}</span>
                      <span className="pm-pa-option-meta">
                        {readable(account?.product, "Product")} •{" "}
                        {readable(account?.accountType, "Workspace type")}
                      </span>
                    </span>
                    {selected ? <span className="pm-pa-option-check">Selected</span> : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WorkspacePicker;
